"use client";

import { useId, type ReactNode } from "react";
import { useTranslations } from "use-intl";

import { resolveAlbumCardAddToListsTarget } from "@/components/album-card-add-to-lists-target";
import { AlbumCardDetails } from "@/components/album-card-details";
import { AlbumCardImage } from "@/components/album-card-image";
import { Link, type LinkMask, type LinkTarget } from "@/components/link";
import {
  type OfferSummary,
  type OfferSummaryBadgeMode,
  type OfferSummaryBadgeVariant,
} from "@/components/marketplace/offer-summary-badge";
import { TrackingScope } from "@/components/TrackingScope";
import { useLists } from "@/contexts/lists-context";
import { useHydrated } from "@/hooks/use-hydrated";
import type { AddToListsTarget } from "@/lib/add-to-lists-target";
import { cn } from "@/lib/utils";
import { ALBUM_TYPES, type AlbumType } from "@garimpa/lib/albumTypes";
import { defaultIfUndefined } from "@garimpa/lib/defaultIfUndefined";
import { buildImageSrcSet, withImageRenderParams } from "@garimpa/lib/imageUrls";
import type { OfferSummaryByCondition } from "@garimpa/lib/offerConditions";

function isAlbumType(value: string): value is AlbumType {
  return (ALBUM_TYPES as readonly string[]).includes(value);
}

interface AlbumCardProps {
  /** Native link target — the router encodes `search` (no hand-built URLs). A
   * bare path string (e.g. a list-item link) is just `to` with no params/search. */
  to?: string;
  params?: Record<string, string>;
  search?: Record<string, unknown>;
  mask?: LinkMask;
  imageUrl: string | null | undefined;
  imageSizes?: string;
  title: string;
  subtitle?: string | null;
  releaseId?: string | null;
  workId?: string;
  addToListsTarget?: AddToListsTarget;
  hideActions?: boolean;
  priority?: boolean;
  /** Album type classification key (album, compilation, live, single_ep, video, other) */
  albumType?: string | null;
  /** Optional listing badge data */
  listingCount?: number;
  listingMinPrice?: string;
  listingMaxPrice?: string;
  listingCurrency?: string;
  offerSummary?: OfferSummary | null;
  offerSummaryByCondition?: OfferSummaryByCondition<OfferSummary>;
  offerDestination?: LinkTarget;
  offerSummaryMode?: OfferSummaryBadgeMode;
  offerSummaryVariant?: OfferSummaryBadgeVariant;
  tags?: ReactNode;
  footer?: ReactNode;
  className?: string;
  linkCoversFooter?: boolean;
}

export function AlbumCard(props: AlbumCardProps) {
  const {
    to,
    params,
    search,
    mask,
    imageUrl,
    title,
    subtitle,
    releaseId,
    workId,
    addToListsTarget,
    albumType,
    listingCount,
    listingMinPrice,
    listingMaxPrice,
    listingCurrency,
    offerSummary,
    offerSummaryMode,
    offerSummaryVariant,
    tags,
    footer,
    className,
  } = props;
  const priority = defaultIfUndefined(props.priority, false);
  const linkCoversFooter = defaultIfUndefined(props.linkCoversFooter, false);
  const hideActions = defaultIfUndefined(props.hideActions, false);
  const t = useTranslations("globals.search.albumType");
  const { isInCollection } = useLists();
  const hydrated = useHydrated();
  const inCollection = hydrated && isInCollection({ workId });
  const resolvedAddToListsTarget = resolveAlbumCardAddToListsTarget(addToListsTarget, workId);
  const albumTypeLabel = albumType && isAlbumType(albumType) ? t(albumType) : null;
  const renderedImageUrl = withImageRenderParams(imageUrl, { width: 320 });
  const imageSrcSet = buildImageSrcSet(imageUrl, [320, 480, 640]);
  const headingId = useId();

  const primaryLink = (
    <Link
      to={to}
      params={params}
      search={search}
      mask={mask}
      onClick={() => {}}
      aria-labelledby={headingId}
      className="absolute inset-0 z-10 rounded-xl focus-visible:outline-none"
    />
  );

  const primaryContent = (
    <div className="relative min-w-0 flex-1">
      {!linkCoversFooter ? primaryLink : null}
      <AlbumCardImage
        hideActions={hideActions}
        imageSrcSet={imageSrcSet}
        imageSizes={props.imageSizes}
        imageUrl={renderedImageUrl}
        inCollection={inCollection}
        priority={priority}
        addToListsTarget={resolvedAddToListsTarget}
        title={title}
      />
      <AlbumCardDetails
        albumTypeLabel={albumTypeLabel}
        footer={footer}
        headingId={headingId}
        listingCount={listingCount}
        listingCurrency={listingCurrency}
        listingMaxPrice={listingMaxPrice}
        listingMinPrice={listingMinPrice}
        offerSummary={offerSummary}
        offerSummaryByCondition={props.offerSummaryByCondition}
        offerDestination={props.offerDestination ?? { to: to ?? "/work/$id", params, search, mask }}
        offerSummaryMode={offerSummaryMode}
        offerSummaryVariant={offerSummaryVariant}
        releaseId={releaseId}
        subtitle={subtitle}
        tags={tags}
        title={title}
      />
    </div>
  );

  return (
    <TrackingScope entityType={resolvedAddToListsTarget?.entityType}>
      <div
        className={cn(
          "group relative flex h-full min-w-0 flex-col overflow-hidden rounded-xl border-2 border-transparent bg-card transition-colors hover:border-primary focus-within:border-primary",
          className,
        )}
      >
        {linkCoversFooter ? primaryLink : null}
        {primaryContent}
        {footer ? <div className="px-2 pb-2">{footer}</div> : null}
      </div>
    </TrackingScope>
  );
}
